import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import RegistrationForm from "@/components/RegistrationForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/logo.png";

export default function LandingPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const handleSubmit = async (formData) => {
    try {
      const res = await axios.post(`${API}/registrations`, formData);
      setSubmittedName(res.data.full_name || formData.full_name);
      setSubmitted(true);
      toast.success("Registration received");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error("Submission issue", { description: String(detail) });
      throw err;
    }
  };

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen w-full"
      style={{
        backgroundImage:
          "linear-gradient(180deg, rgba(255,253,248,0.30) 0%, rgba(255,253,248,0.12) 40%, rgba(255,253,248,0.40) 100%), url('/bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center"
        >
          <img
            src={LOGO_URL}
            alt="Shepherd's Academy"
            data-testid="hero-logo"
            className="w-48 md:w-64 h-auto crest-glow"
          />
        </motion.div>

        {/* Tagline */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-title"
          className="font-serif-display mt-10 text-center text-4xl md:text-5xl text-[#1A1516] tracking-tight leading-[1.1]"
          style={{
            textShadow: "0 1px 2px rgba(255,255,255,0.85), 0 0 24px rgba(255,255,255,0.65)",
          }}
        >
          Preparing Pastors
          <br />
          <span className="italic text-[#6B1F2A]">for Effective Ministry</span>
        </motion.h1>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12"
        >
          {!submitted ? (
            <RegistrationForm onSubmit={handleSubmit} />
          ) : (
            <SuccessPanel
              name={submittedName}
              onReset={() => {
                setSubmitted(false);
                setSubmittedName("");
              }}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
}

function SuccessPanel({ name, onReset }) {
  return (
    <div
      data-testid="success-panel"
      className="bg-white border border-[#EBE8E3] p-10 md:p-14 text-center shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]"
    >
      <div className="mx-auto w-14 h-14 rounded-full border border-[#B8860B] flex items-center justify-center mb-6">
        <CheckCircle2 className="w-7 h-7" style={{ color: "#B8860B" }} strokeWidth={1.4} />
      </div>
      <div className="eyebrow mb-3" style={{ color: "#6B1F2A" }}>
        Registration Received
      </div>
      <h3 className="font-serif-display text-3xl md:text-4xl text-[#1A1516] tracking-tight">
        Thank you{name ? `, ${name}` : ""}.
      </h3>
      <button
        type="button"
        onClick={onReset}
        data-testid="success-reset-button"
        className="mt-8 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#6B1F2A] border-b border-[#B8860B] pb-1 hover:text-[#5A1923] transition-colors"
      >
        Submit another registration
      </button>
    </div>
  );
}
